/**
 * TruePrice Canvas Navigation — Reduced Motion Hook
 * 
 * Respects user's system preference for reduced motion.
 * When enabled, animations should be instant or very subtle.
 */

import { useState, useEffect } from 'react';

/**
 * Hook to detect user's prefers-reduced-motion setting
 * 
 * @returns boolean - true if user prefers reduced motion
 * 
 * Usage:
 * ```tsx
 * const prefersReducedMotion = useReducedMotion();
 * 
 * const transition = prefersReducedMotion 
 *   ? { duration: 0 } 
 *   : { duration: 0.3, ease: [0.32, 0.72, 0, 1] };
 * ```
 */
export function useReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check if window is available (SSR safety)
    if (typeof window === 'undefined') return;

    // Create media query
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    // Set initial value
    setPrefersReducedMotion(mediaQuery.matches);

    // Listen for changes
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } 
    // Fallback for older browsers
    else {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  return prefersReducedMotion;
}

/**
 * Get animation duration based on reduced motion preference
 * Returns 0 if reduced motion is preferred, otherwise returns the provided duration
 */
export function getAnimationDuration(
  duration: number, 
  prefersReducedMotion: boolean
): number {
  return prefersReducedMotion ? 0 : duration;
}

/**
 * Get transition config that respects reduced motion
 * For use with Framer Motion
 */
export function getAccessibleTransition(
  prefersReducedMotion: boolean,
  normalTransition: object
): object {
  if (prefersReducedMotion) {
    return { duration: 0 };
  }
  return normalTransition;
}

export default useReducedMotion;
